import React, { useMemo } from 'react';
import { Rep, useGetRepsExternalUseQuery } from '../services/repRetrievalService';
import contactUsIllustration from './assets/contactUsIllustration.svg';
import { ContactHeader } from './components/ContactHeader';
import { DropdownContainer } from './components/DropdownContainer';
import { HelpOutlineIcon } from 'drift-ui';
import { FAQContainer } from './components/FAQ/FAQContainer';
import { ContactUsReps } from './components/Reps/ContactUsReps';
import { useAppSelector } from '../store';
import { TechnicalHelpSection } from './components/TechHelp';
import { ContactUsSkeleton } from './SkeletonLoading';
import { NoFeatures } from './components/Reps/NoFeatures';
import styles from './styles.scss';
import { useTranslation } from 'react-i18next';
import { useGetCarrierPermissionsQuery } from '../services/freightPermissionService';
import UserPermissionsLevelForm from './components/UserPermissionsLevelForm';
import cx from 'classnames';
import { FeatureFlags } from '@Common/featuresFlagsTypes';

export type RepData = Rep | undefined;
type RepDataMemoReturn = RepData[] | undefined;

export const ContactUs = () => {
  const userEmail = useAppSelector((state) => state.adminShell.userEmail);
  const authType = useAppSelector((state) => state.adminShell.authed);
  const { userFeatures, userCompanyData } = useAppSelector((state) => state.adminShell);
  const freightPermissionsEnabled = userFeatures?.includes(FeatureFlags.FREIGHT_PERMISSIONS_WEB);
  const isUserLogged = authType === 'user';
  const { data, isLoading } = useGetRepsExternalUseQuery(userEmail!, { skip: !isUserLogged });

  const { data: carrierPermissionsData, isLoading: carrierPermissionsLoading } = useGetCarrierPermissionsQuery(
    userCompanyData?.mcleod_carrier_id,
    {
      skip: !freightPermissionsEnabled,
    },
  );

  const repsData = useMemo<RepDataMemoReturn>((): RepDataMemoReturn => {
    if (data?.some((rep) => rep.name)) {
      const fuelData = data.find((rep) => rep.product === 'Carrier Services');
      const factoringData = data.find((rep) => rep.product === 'RTS Financial');
      const proTransportData = data.find((rep) => rep.product === 'ProTransport');
      const freightData = data.find((rep) => rep.product === 'Ryan Transportation');
      return [fuelData, factoringData, proTransportData, freightData];
    }
  }, [data]);

  return (
    <div className={cx(styles.container, { [styles.containerWithFreight]: freightPermissionsEnabled })}>
      {isLoading || carrierPermissionsLoading ? (
        <ContactUsSkeleton />
      ) : (
        <>
          <div
            className={cx(
              styles.bodyContainer,
              freightPermissionsEnabled ? styles.bodyContainerWithFreight : styles.bodyContainerWidht,
            )}
          >
            <ContactHeader />
            {isUserLogged && (
              <DropdownContainer content={<FAQBody />} testId="faq">
                <FAQContainer />
              </DropdownContainer>
            )}
            {isUserLogged && (
              <DropdownContainer
                content={'Get help with your accounts.'}
                initialOpen={!!repsData}
                testId="getHelpWithYourAccounts"
              >
                {repsData ? (
                  <ContactUsReps
                    repsData={repsData}
                    freightPermissionsFF={freightPermissionsEnabled}
                    carrierPermissionsData={carrierPermissionsData}
                  />
                ) : (
                  <NoFeatures freightPermissionsFF={freightPermissionsEnabled} />
                )}
              </DropdownContainer>
            )}
            <DropdownContainer content={'Get technical help.'} initialOpen testId="getTechnicalHelp">
              <TechnicalHelpSection />
            </DropdownContainer>
          </div>
          <div
            className={cx(freightPermissionsEnabled ? styles.contactUsImgContainer : styles.contactUsImageContainer)}
          >
            <img src={contactUsIllustration} className={cx(freightPermissionsEnabled && styles.contactUsImg)} />
          </div>
        </>
      )}
      <UserPermissionsLevelForm currentUserEmail={userEmail!} />
    </div>
  );
};

const FAQBody = () => {
  const { t } = useTranslation();
  return (
    <>
      <HelpOutlineIcon className={styles.icon} /> {t('faq')}
    </>
  );
};
